<template>
  <div>
    <v-form @submit.prevent="submitResponse">
      <h2 class="mb-4">Electrochemistry Results - Part C</h2>

      <p class="mb-3">
        In this part of the experiment, choose the pair of half-cells that have the closest
        theoretical cell potential that is LESS THAN the target voltage. Fill in the information in
        the data table below summarizing your results from this part of the experiment.
        <b>Please express the percent error value to 3 significant figures.</b>
      </p>

      <p class="mb-3">
        For this part of the experiment, please use the following standard reduction potentials:
      </p>

      <v-simple-table style="max-width: 319px" class="ml-8 mb-5">
        <tbody>
          <tr>
            <td><stemble-latex content="$\ce{Cu^2+ + 2e^- -> Cu}$" /></td>
            <td style="text-align: right"><stemble-latex content="$+0.337~\text{V}$" /></td>
          </tr>

          <tr>
            <td><stemble-latex content="$\ce{Sn^2+ + 2e^- -> Sn}$" /></td>
            <td style="text-align: right"><stemble-latex content="$-0.1375~\text{V}$" /></td>
          </tr>

          <tr>
            <td><stemble-latex content="$\ce{Zn^2+ + 2e^- -> Zn}$" /></td>
            <td style="text-align: right"><stemble-latex content="$-0.7618~\text{V}$" /></td>
          </tr>
        </tbody>
      </v-simple-table>

      <v-simple-table>
        <thead>
          <tr>
            <td>
              <stemble-latex content="$\textbf{Variable}$" />
            </td>
            <td style="text-align: center">
              <stemble-latex content="$\textbf{Value}$" />
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <stemble-latex content="$\text{Target}$" />
              <stemble-latex content="$\text{ E}_\text{cell}$" />
              <stemble-latex content="$\text{ (V)}$" />
            </td>
            <td class="py-2 my-2" style="text-align: center">
              <latex-number :number="targetVoltage.toFixed(3)" />
            </td>
          </tr>
          <tr>
            <td>
              <stemble-latex content="$\text{i) V}_\ce{stock}$" />
              <stemble-latex content="$\text{ in Vol}$" />
              <stemble-latex content="$\text{ Flask 1}$" />
              <stemble-latex content="$\text{ (mL)}$" />
            </td>
            <td class="centered-input py-2 my-2">
              <calculation-input
                v-model="inputs.volDil1"
                :show-scientific-notation="false"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>
              <stemble-latex content="$\ce{ii) [C]}$" />
              <stemble-latex content="$\text{ in Vol}$" />
              <stemble-latex content="$\text{ Flask 1}$" />
              <stemble-latex content="$\text{ (M)}$" />
            </td>
            <td class="centered-input py-2 my-2">
              <calculation-input
                v-model="inputs.concFlask1"
                :show-scientific-notation="false"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>
              <stemble-latex content="$\text{iii) V}_\ce{dil}$" />
              <stemble-latex content="$\text{ in Vol}$" />
              <stemble-latex content="$\text{ Flask 2}$" />
              <stemble-latex content="$\text{ (mL)}$" />
            </td>
            <td class="centered-input py-2 my-2">
              <calculation-input
                v-model="inputs.volDil2"
                :show-scientific-notation="false"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>
              <stemble-latex content="$\ce{iv) [C]}$" />
              <stemble-latex content="$\text{ in Vol}$" />
              <stemble-latex content="$\text{ Flask 2}$" />
              <stemble-latex content="$\text{ (M)}$" />
            </td>
            <td class="centered-input py-2 my-2">
              <calculation-input
                v-model="inputs.concFlask2"
                :show-scientific-notation="false"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>
              <stemble-latex content="$\text{v) E}_\text{cell,obs}$" />
              <stemble-latex content="$\text{ (V)}$" />
            </td>
            <td class="centered-input py-2 my-2">
              <calculation-input
                v-model="inputs.EcellObs"
                :show-scientific-notation="false"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>
              <stemble-latex content="$\text{vi) }\%\,\text{Error}$" />
            </td>
            <td class="centered-input py-2 my-2">
              <calculation-input
                v-model="inputs.pctError"
                :show-scientific-notation="false"
                :disabled="!allowEditing"
              />
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import LatexNumber from '@/tasks/components/displayers/LatexNumber.vue';

export default {
  name: 'ChemMcMA5ResultsCV3',
  components: {
    LatexNumber,
    CalculationInput,
    StembleLatex,
  },
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        volDil1: null,
        concFlask1: null,
        volDil2: null,
        concFlask2: null,
        EcellObs: null,
        EcellTheoretical: null,
        pctError: null,
      },
      attachments: [],
    };
  },
  computed: {
    versionNumber() {
      return this.taskState.getValueBySymbol('versionNumber').content;
    },
    voltageChange() {
      return this.taskState.getValueBySymbol('voltageChange').content;
    },
    baseVoltage() {
      if (this.versionNumber.value === 1) {
        return 1.102; // Zn and Cu
      } else if (this.versionNumber.value === 2) {
        return 0.4745; // Sn and Cu
      } else {
        return 0.6243; // Zn and Sn
      }
    },
    targetVoltage() {
      return this.baseVoltage + this.voltageChange.toFloat();
    },
  },
};
</script>
<style scoped></style>
